import classNames from 'classnames'
import type {ReactNode} from 'react'
import React, {useCallback} from 'react'

import type {pipelineHeadFragment$key} from '../../../hooks/__generated__/pipelineHeadFragment.graphql'
import useDocumentClientHeight from '../../../hooks/useDocumentClientHeight'
import useLocalStorageValue from '../../../hooks/useLocalStorageValue'
import useTopOffset from '../../../hooks/useTopOffset'
import useWindowWidth from '../../../hooks/useWindowWidth'
import SettingsSidebar from '../SettingsSidebar/SettingsSidebar'

import {getMaxSidebarWidth, getSidebarWidth, MIN_SIDEBAR_WIDTH} from './PageWithSidebar.utils'

import styles from './PageWithSidebar.css'

type Props = {
  className?: string
  children: ReactNode
  isOpenSidebar?: boolean
  pipelineHeadKey: pipelineHeadFragment$key | null
}

function PageWithSidebar({className, children, isOpenSidebar = true, pipelineHeadKey}: Props) {
  const documentClientHeight = useDocumentClientHeight()
  const {topOffset, topOffsetRef} = useTopOffset()
  const windowWidth = useWindowWidth()

  const [sidebarResizeDeltaValue, setSidebarResizeDeltaValue] = useLocalStorageValue(
    'jb.teamcity.pipelines.settingsSidebar.resizeDelta',
  )
  const sidebarResizeDelta =
    sidebarResizeDeltaValue != null ? parseInt(sidebarResizeDeltaValue, 10) : 0

  const maxSidebarWidth = getMaxSidebarWidth(windowWidth)
  const sidebarWidth = Math.min(getSidebarWidth(windowWidth, sidebarResizeDelta), maxSidebarWidth)

  const onSidebarResize = useCallback(
    (deltaWidth: number) => {
      const newSidebarWidth = sidebarWidth + deltaWidth
      setSidebarResizeDeltaValue((newSidebarWidth - getSidebarWidth(windowWidth, 0)).toString())
    },
    [setSidebarResizeDeltaValue, sidebarWidth, windowWidth],
  )

  return (
    <div
      className={classNames(className, styles.container)}
      ref={topOffsetRef}
      style={{height: topOffset != null ? `${documentClientHeight - topOffset}px` : undefined}}
    >
      {topOffset != null && (
        <>
          <div
            className={styles.main}
            style={{paddingRight: isOpenSidebar ? `${sidebarWidth}px` : undefined}}
          >
            {children}
          </div>
          <SettingsSidebar
            maxWidth={maxSidebarWidth}
            minWidth={MIN_SIDEBAR_WIDTH}
            sidebarWidth={sidebarWidth}
            isOpen={isOpenSidebar}
            onResize={onSidebarResize}
            pipelineHeadKey={pipelineHeadKey}
          />
        </>
      )}
    </div>
  )
}

export default PageWithSidebar
